import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { TitleContainer, Wrapper } from "../../style";
import ColumnContainer from "../../../common/ColumnContainer";
import RowContainer from "../../../common/RowContainer";
import { IconButton, TextField, Typography } from "@mui/material";
import {
  ArrowBackIosRounded,
  CheckRounded,
  DeleteForever,
} from "@mui/icons-material";
import { userDashboardRoute } from "../../../../utils/routes/Routes";
import { useDispatch, useSelector } from "react-redux";
import "./TicketsStyle.less";
import AutocompleteComponent from "../../../common/AutocompleteComponent";
import { useEffect, useState } from "react";
import UploadFileComponent from "../../../common/UploadFileComponent";
import { DashboardButton } from "../../../common/DashboardButton";
import { SnackbarComponent } from "../../../common/SnackbarComponent";
import { selectAuthData } from "../../../../redux/features/auth/authSlice";
import {
  useCreateTicketMutation,
  useGetTicketCategoriesQuery,
} from "../../../../redux/features/tickets/ticketsApi";
import {
  getTicketCategories,
  selectTicketCreating,
} from "../../../../redux/features/tickets/ticketsSlice";
import { FormattedMessage, useIntl } from "react-intl";
import { translateFilterKeys } from "../../../../utils/helpers/translateFilterKeys";
import { selectLanguage } from "../../../../redux/features/language/languageSlice";

const NewTicket = () => {
  // INIT
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const intl = useIntl();

  // STATE
  const [selection, setSelection] = useState({});
  const [fileUploaded, setFileUploaded] = useState(null);
  const [fileInputRef, setFileInputRef] = useState(null);
  const [openSnackbarFile, setOpenSnackbarFile] = useState(false);
  const [openRequiredFieldsSnackbar, setOpenRequiredFieldsSnackbar] =
    useState(false);
  const [fileSizeError, setFileSizeError] = useState(false);

  // REDUX
  const newTicketDetails = useSelector(selectTicketCreating);
  const { projectId } = newTicketDetails;
  const currentLanguage = useSelector(selectLanguage);

  const {
    data: ticketCategories,
    isLoading: isLoadingCategories,
    isError: isErrorCategories,
  } = useGetTicketCategoriesQuery();
  const [addNewTicket, { isLoading, isError, isSuccess }] =
    useCreateTicketMutation();
  const userData = useSelector(selectAuthData);
  const userId = userData?.userData?.id;

  // EFFECTS
  useEffect(() => {
    if (!isLoadingCategories && !isErrorCategories) {
      dispatch(getTicketCategories(ticketCategories));
    }
  }, [ticketCategories, isLoadingCategories, isErrorCategories, dispatch]);

  // FUNCTIONS
  const handleGoBackToProject = () => {
    navigate(`${userDashboardRoute.projects}/${projectId}`);
  };

  const getSelectedValue = (categories) => {
    if (categories) {
      const value = ticketCategories.find(
        (item) => item.id === selection.category_id
      );
      if (value) {
        return value;
      }
    }
    return null;
  };

  const handleSelectionChange = (value, key, isCategory) => {
    if (isCategory) {
      setSelection((prevState) => ({
        ...prevState,
        [key.id]: value.id,
      }));
    } else {
      setSelection((prevState) => ({
        ...prevState,
        [key.id]: value,
      }));
    }
  };
  const handleFileUpload = (fileUploaded) => {
    setFileUploaded(fileUploaded);
  };

  const handleFileChange = (file) => {
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (["zip", "mod", "bin"].includes(fileExtension)) {
        setFileUploaded(file);
      } else {
        setOpenSnackbarFile(true);
      }
    }
  };

  const handleFileInputRefChange = (fileInput) => {
    setFileInputRef(fileInput);
  };

  const handleDeleteFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFileUploaded(null);
  };

  const handleFileSizeError = (isError) => {
    setFileSizeError(isError);
  };

  const handleSubmit = async () => {
    if (selection.title && selection.category_id && selection.description) {
      const ticketFileData = new FormData();
      ticketFileData.append("title", selection.title);
      ticketFileData.append("description", selection.description);
      ticketFileData.append("category_id", selection.category_id);
      ticketFileData.append("company_name", userData?.userData?.company_name);

      if (fileUploaded) {
        ticketFileData.append("file", fileUploaded);
      }

      try {
        const response = await addNewTicket({
          userId,
          projectId,
          formData: ticketFileData,
        }).unwrap();

        navigate(`${userDashboardRoute.tickets}/${response.new_ticket_id}`);
      } catch (error) {
        console.log(error);
      }
    } else {
      setOpenRequiredFieldsSnackbar(true);
    }
  };

  // UTILS

  return (
    <>
      {ticketId ? (
        <Outlet />
      ) : (
        <Wrapper className="new-ticket">
          <ColumnContainer start>
            <ColumnContainer>
              <TitleContainer>
                <ColumnContainer noGap>
                  <RowContainer align="center">
                    <RowContainer minGap start align="center">
                      <IconButton>
                        <ArrowBackIosRounded onClick={handleGoBackToProject} />
                      </IconButton>
                      <Typography variant="h3" component="h3">
                        <FormattedMessage id="ticket.new.title" />
                      </Typography>
                    </RowContainer>
                    <Link to={`${userDashboardRoute.projects}/${projectId}`}>
                      <Typography variant="p" component="p">
                        <FormattedMessage id="ticket.new.project" /> #
                        {projectId}
                      </Typography>
                    </Link>
                  </RowContainer>
                </ColumnContainer>
              </TitleContainer>
            </ColumnContainer>

            <ColumnContainer>
              <ColumnContainer minGap>
                <Typography variant="p" component="p" className="titles-info">
                  <FormattedMessage id="ticket.new.name" />*
                </Typography>
                <TextField
                  autoComplete="off"
                  label=""
                  fullWidth
                  required={true}
                  variant="outlined"
                  value={selection.title || ""}
                  onChange={(e) =>
                    handleSelectionChange(e.target.value, { id: "title" })
                  }
                />
              </ColumnContainer>
              <ColumnContainer minGap>
                <Typography variant="p" component="p" className="titles-info">
                  <FormattedMessage id="ticket.new.category" />*
                </Typography>
                <AutocompleteComponent
                  value={getSelectedValue(ticketCategories)}
                  onChange={(e, value) => {
                    handleSelectionChange(value, { id: "category_id" }, true);
                  }}
                  options={ticketCategories || []}
                  getOptionLabel={(option) =>
                    translateFilterKeys(option?.name, currentLanguage) || ""
                  }
                  getOptionSelected={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      variant="filled"
                      label=""
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </ColumnContainer>
              <ColumnContainer minGap>
                <Typography variant="p" component="p" className="titles-info">
                  <FormattedMessage id="ticket.new.description" />*
                </Typography>
                <TextField
                  autoComplete="off"
                  multiline
                  rows={4}
                  maxLength={500}
                  size="medium"
                  fullWidth
                  value={selection.description || ""}
                  InputProps={{
                    inputProps: {
                      sx: {
                        "&::placeholder": {
                          color: "var(--text-secondary)",
                          opacity: 0.7,
                        },
                      },
                    },
                  }}
                  onChange={(e) =>
                    handleSelectionChange(e.target.value, { id: "description" })
                  }
                  placeholder={intl.formatMessage({
                    id: "ticket.new.description.max",
                  })}
                />
              </ColumnContainer>
              <ColumnContainer minGap>
                <Typography variant="p" component="p" className="titles-info">
                  <FormattedMessage id="ticket.new.attachment" />
                </Typography>
                <UploadFileComponent
                  onFileSizeError={handleFileSizeError}
                  acceptedFormatsString={".zip, .mod, .bin"}
                  onFileUpload={handleFileUpload}
                  onFileChange={handleFileChange}
                  onFileInputRefChange={handleFileInputRefChange}
                  instructions={<FormattedMessage id="file.attach" />}
                >
                  <RowContainer className="success-upload-icons" center>
                    {fileUploaded ? (
                      <RowContainer center minGap align="end">
                        <CheckRounded />
                        <Typography component="p" fontSize={"16px"}>
                          {fileUploaded?.name}
                        </Typography>
                        <DeleteForever onClick={(e) => handleDeleteFile(e)} />
                      </RowContainer>
                    ) : fileSizeError ? (
                      <Typography
                        component="p"
                        fontSize={"16px"}
                        color={"var(--error)"}
                        textAlign={"center"}
                      >
                        {<FormattedMessage id="file.size.error" />}
                      </Typography>
                    ) : (
                      <Typography component="p" fontSize={"16px"}>
                        <FormattedMessage id="file.noFile" />
                      </Typography>
                    )}
                  </RowContainer>
                </UploadFileComponent>
              </ColumnContainer>
            </ColumnContainer>
            <RowContainer end>
              <DashboardButton
                type="filled"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                <FormattedMessage id="button.createTicket" />
              </DashboardButton>
            </RowContainer>
            <SnackbarComponent
              message={<FormattedMessage id="snackbar.file.format" />}
              duration={5000}
              open={openSnackbarFile}
              handleClose={() => setOpenSnackbarFile(false)}
            ></SnackbarComponent>
            <SnackbarComponent
              message={<FormattedMessage id="snackbar.form" />}
              duration={5000}
              open={openRequiredFieldsSnackbar}
              handleClose={() => setOpenRequiredFieldsSnackbar(false)}
            ></SnackbarComponent>
          </ColumnContainer>
        </Wrapper>
      )}
    </>
  );
};

export default NewTicket;
